/**
 * Order service for Custom Backend operations
 * Handles all order-related API calls
 */

import { BaseCustomService } from "../services/base";
import { IOptionsRequest, IResponse } from "../../../models";
import { formatResponse } from "../../../core/utils";
import { ICustomConfig } from "../interface";
import { Logger } from "../../../logger";

export class OrderService extends BaseCustomService {
  constructor(baseUrl: string, config: ICustomConfig, logger?: Logger) {
    super(baseUrl, config, logger);
  }

  /**
   * Get order history for a customer
   */
  async getOrderHistory(
    customerAccessToken: string,
    page = 1,
    limit = 10,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    try {
      this.logger.info("Fetching order history", {
        customerAccessToken: customerAccessToken
          ? `${customerAccessToken.substring(0, 8)}...`
          : "null",
        page,
        limit,
      });

      // Add access token to headers if provided
      const requestOptions = { ...options };
      if (customerAccessToken) {
        requestOptions.headers = {
          ...requestOptions.headers,
          "gk-access-token": customerAccessToken,
        };
      }

      const params = {
        page: page.toString(),
        limit: limit.toString(),
      };

      const result = await this.request(
        "/auth/customers/orders",
        "GET",
        undefined,
        {
          params,
          ...requestOptions,
        }
      );

      this.logger.info("Successfully fetched order history", {
        orderCount: result.orders?.length || 0,
        pagination: result.pagination,
      });

      // Extract user information from the first order's shipping address and email
      let user = null;
      if (result.orders && result.orders.length > 0) {
        const firstOrder = result.orders[0]?.order;
        if (firstOrder) {
          const shippingAddress = firstOrder.shipping_address;
          user = {
            email: firstOrder.email || "",
            phone: shippingAddress?.phone || "",
          };
        }
      }

      // Return structured data with user information
      return formatResponse({
        success: true,
        message: "Order history retrieved successfully.",
        data: {
          ...result,
          user,
        },
        meta: {
          pagination: result.pagination,
        },
      });
    } catch (error: any) {
      this.logger.error(
        "Failed to get order history",
        error instanceof Error ? error : new Error(String(error)),
        {
          customerAccessToken: customerAccessToken
            ? `${customerAccessToken.substring(0, 8)}...`
            : "null",
          page,
          limit,
        }
      );

      // Return empty data structure on error
      return formatResponse({
        success: false,
        message: "Failed to retrieve order history.",
        data: {
          orders: [],
          pagination: {
            total: 0,
            page: page,
            limit: limit,
            totalPages: 0,
            hasNextPage: false,
            hasPreviousPage: false,
          },
          user: null,
        },
        error: {
          message: error instanceof Error ? error.message : String(error),
          details: error,
        },
      });
    }
  }

  /**
   * Get a single order by ID
   */
  async getOrderById(
    customerAccessToken: string,
    orderId: string,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    try {
      this.logger.info("Fetching order by ID", {
        customerAccessToken: customerAccessToken
          ? `${customerAccessToken.substring(0, 8)}...`
          : "null",
        orderId,
      });

      // Add access token to headers if provided
      const requestOptions = { ...options };
      if (customerAccessToken) {
        requestOptions.headers = {
          ...requestOptions.headers,
          "gk-access-token": customerAccessToken,
        };
      }

      const result = await this.request(
        `/auth/customers/orders/${orderId}`,
        "GET",
        undefined,
        requestOptions
      );

      this.logger.info("Successfully fetched order", {
        orderId: result.order?.id,
        orderNumber: result.order?.order_number,
      });

      return formatResponse({
        success: true,
        message: "Order retrieved successfully.",
        data: result.order,
        meta: undefined,
      });
    } catch (error: any) {
      this.logger.error(
        "Failed to get order by ID",
        error instanceof Error ? error : new Error(String(error)),
        {
          customerAccessToken: customerAccessToken
            ? `${customerAccessToken.substring(0, 8)}...`
            : "null",
          orderId,
        }
      );

      // Return null on error
      return formatResponse({
        success: false,
        message: "Failed to retrieve order.",
        data: null,
        error: {
          message: error instanceof Error ? error.message : String(error),
          details: error,
        },
      });
    }
  }
}
